interface CurrencyOptions {
  currency: string;
  locale: string;
  cents: boolean;
  decimalPlaces: number;
}

interface NumberOptions {
  locale: string;
  minDigits: number;
  maxDigits: number;
  cents: boolean;
}

export function formatCurrency(
  amount: number,
  options: Partial<CurrencyOptions> = {}
): string {
  const defaultOptions: CurrencyOptions = {
    currency: 'USD',
    locale: 'en-US',
    cents: false,
    decimalPlaces: 2,
  };
  const { currency, locale, cents, decimalPlaces } = {
    ...defaultOptions,
    ...options,
  };

  if (cents) amount = amount / 100;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(amount);
}

export function formatNumber(
  number: number,
  options: Partial<NumberOptions> = {
    locale: 'en-US',
    minDigits: 0,
    maxDigits: 0,
  }
): string {
  const { locale } = options;

  const formatOptions = {
    minimumFractionDigits: options.minDigits,
    maximumFractionDigits: options.maxDigits,
  };

  return new Intl.NumberFormat(locale, formatOptions).format(number);
}

export function formatSimpleNumber(
  number: number,
  options: Partial<NumberOptions> = {
    locale: 'en-US',
    minDigits: 0,
    maxDigits: 0,
    cents: false,
  }
): string {
  const { locale, cents } = options;

  const formatOptions = {
    minimumFractionDigits: options.minDigits,
    maximumFractionDigits: options.maxDigits,
  };
  if (cents) number = number / 100;

  if (number >= 1000 && number < 1000000) {
    return `${parseFloat((number / 1000).toFixed(1))}K`;
  } else if (number >= 1000000) {
    return `${parseFloat((number / 1000000).toFixed(1))}M`;
  }

  return new Intl.NumberFormat(locale, formatOptions).format(number);
}

export function formatOrdinal(number: number): string {
  const suffixes = {
    one: 'st',
    two: 'nd',
    three: 'rd',
    other: 'th',
  };

  const lastDigit = number % 10;
  const lastTwoDigits = number % 100;

  // Handle special cases for 11, 12, 13
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${number}${suffixes.other}`;
  }

  // Handle other cases
  switch (lastDigit) {
    case 1:
      return `${number}${suffixes.one}`;
    case 2:
      return `${number}${suffixes.two}`;
    case 3:
      return `${number}${suffixes.three}`;
    default:
      return `${number}${suffixes.other}`;
  }
}

export const range = (start: number, end: number): number[] => {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

export function formatCoupon(coupon?: any): string {
  if (!coupon) return '';

  let description = '';

  if (coupon.amountOff) {
    const currency = coupon.currency || 'USD';
    const value = coupon.amountOff / 100;
    description = `${formatCurrency(value, {
      currency,
      decimalPlaces: 2,
    })} off`;
  } else if (coupon.percentOff) {
    description = `${coupon.percentOff}% off`;
  }

  if (coupon.durationInMonths) {
    description += ` for ${coupon.durationInMonths} ${
      coupon.durationInMonths === 1 ? 'month' : 'months'
    }`;
  } else if (coupon.duration === 'forever') {
    description += ' forever';
  } else {
    description += ' one time';
  }

  return description;
}
